import { useRouter } from "next/router"
import { type PropsWithChildren, useEffect, useState } from "react"
import { useSafeContext } from "@oefr/hooks"
import { getCookie, setCookie } from "cookies-next/client"
import { addMonths } from "date-fns"
import { toSnakeCase } from "remeda"
import { createNamedContext } from "@/utils/create-named-context"

type UTMParams = {
    utmSource?: string
    utmMedium?: string
    utmCampaign?: string
    utmTerm?: string
    utmContent?: string
    gclid?: string
}

type PersistentQueryParamsContext = {
    utmParams: UTMParams | undefined
}

const PersistentQueryParamsContext = createNamedContext<PersistentQueryParamsContext>(
    "PersistentQueryParamsContext"
)

const UTM_PARAMS_COOKIE_KEY = "utm_params"

const COOKIE_OPTIONS = {
    expires: addMonths(new Date(), 1),
}

export function PersistentQueryParamsProvider({ children }: PropsWithChildren) {
    const { query } = useRouter()

    const [utmParams, setUtmParams] = useState(() => {
        const cookie = getCookie(UTM_PARAMS_COOKIE_KEY)

        return cookie ? (JSON.parse(cookie) as UTMParams) : undefined
    })

    useEffect(() => {
        const utmKeys = [
            "utmSource",
            "utmMedium",
            "utmCampaign",
            "utmTerm",
            "utmContent",
            "gclid",
        ] as const

        const newUtmParams = utmKeys.reduce<UTMParams>((utm, key) => {
            const queryKey = toSnakeCase(key)

            if (query[queryKey]) {
                utm[key] = String(query[queryKey])
            }

            return utm
        }, {})

        if (Object.keys(newUtmParams).length > 0) {
            setCookie(UTM_PARAMS_COOKIE_KEY, JSON.stringify(newUtmParams), COOKIE_OPTIONS)
            setUtmParams(newUtmParams)
        }
    }, [query])

    return (
        <PersistentQueryParamsContext.Provider value={{ utmParams }}>
            {children}
        </PersistentQueryParamsContext.Provider>
    )
}

export function usePersistentQueryParamsContext() {
    return useSafeContext(PersistentQueryParamsContext)
}
